import { reaction, observable } from "mobx";
import * as monaco from 'monaco-editor';
import { initComponentStore, noop } from "@/utils/mobx";
import type { MagnifierState } from "./MonacoMagnifier";

export interface LineDecoration {
    startLine: number;
    endLine: number;
    type: 'added' | 'modified';
}

export const BASE_FONT_SIZE = 13;
export const BASE_LINE_HEIGHT = 20;
export const MIN_FONT_SIZE = 8;
export const MAX_FONT_SIZE = 32;

export class MonacoEditorStore {
    // diff mode
    diff: boolean = false;
    original: string = '';
    modified: string = '';

    value: string = '';
    language: string = 'typescript';
    readOnly: boolean = false;
    onChange: (value: string) => void = noop;
    viewStateKey: string | undefined = undefined;
    disableContextMenu: boolean = false;
    disableSelection: boolean = false;
    fontSize: number = BASE_FONT_SIZE;
    onFontSizeChange: (size: number) => void = noop;
    hidden: boolean = false;

    scrollToLine: number | null = null;
    onCtrlClickSymbol: (symbol: string, line: number, col: number) => void = noop;
    onScrollToLineDone: () => void = noop;

    // Merge-conflict CodeLens: enabled for the code editor; a provider renders
    // accept-mine/theirs/both lenses over each conflict region via `getConflicts`.
    enableConflictLens: boolean = false;
    getConflicts: (() => any[]) | null = null;  // reads live conflict regions for the open file
    saveNow: () => void = noop;
    conflictCommandId: string | null = null;  // non-observable; set when the lens command is registered
    // Stash re-apply conflict: label lenses "committed"/"stash" instead of "mine"/"theirs".
    stashConflict: boolean = false;
    // 1-based start line of the conflict last jumped to (navigator or post-resolve). Non-observable.
    _conflictJumpLine: number | null = null;

    // Diff mode: make the modified (right) side editable. onChange fires with the
    // modified content on edit. _lastSyncedModified guards against useMonacoSync
    // clobbering unsaved edits (only re-sync when `modified` genuinely reloads).
    diffModifiedEditable: boolean = false;
    _lastSyncedModified: string | null = null;

    decorationRanges: LineDecoration[] = [];

    wordWrap: boolean = false;

    scrollSyncKey: string | undefined = undefined;
    scrollSyncSection: "code" | "review" | undefined = undefined;

    vkbVisible: boolean = false;
    vkbCollapsed: boolean = true;
    magnifierState: MagnifierState | null = null;
    _vkbHistoryPushed: boolean = false;

    // Editor instance lifecycle — null until the component calls attachEditorInstance.
    // observable.ref: MobX tracks reference changes but never observes Monaco internals.
    editor: monaco.editor.IStandaloneCodeEditor | monaco.editor.IStandaloneDiffEditor | null = null;
    // Decorations collection for standard mode — always set/cleared alongside editor.
    // Non-observable: changes in lockstep with editor, which already triggers the autorun.
    decorationsCollection: monaco.editor.IEditorDecorationsCollection | null = null;
    // Cleanup for find-widget visibility watcher — non-observable.
    _findWidgetCleanup: (() => void) | null = null;

    constructor() {
        initComponentStore(this, {
            _vkbHistoryPushed: false,
            editor: observable.ref,
            decorationsCollection: false,
            _findWidgetCleanup: false,
            conflictCommandId: false,
            _conflictJumpLine: false,
            _lastSyncedModified: false,
        });
        // Close VKB whenever the editor becomes read-only or hidden.
        reaction(() => this.readOnly || this.hidden || (this.diff && !this.diffModifiedEditable), (shouldClose) => {
            if (shouldClose && this.vkbVisible) this.closeVkb();
        });
    }

    // Called by the component once the Monaco editor DOM instance is ready,
    // and again with null on unmount.
    attachEditorInstance(editor: monaco.editor.IStandaloneCodeEditor | monaco.editor.IStandaloneDiffEditor | null) {
        this._findWidgetCleanup?.();
        this._findWidgetCleanup = null;

        this.editor = editor;
        this.decorationsCollection = (!editor || this.diff)
            ? null
            : (editor as monaco.editor.IStandaloneCodeEditor).createDecorationsCollection([]);

        if (editor && !this.diff) {
            const dom = (editor as monaco.editor.IStandaloneCodeEditor).getDomNode();
            if (dom) this._attachFindWidgetWatcher(dom);
        }
    }

    _attachFindWidgetWatcher(dom: HTMLElement) {
        let findWidgetObserver: MutationObserver | null = null;
        let outsideClickHandler: ((e: PointerEvent) => void) | null = null;

        const removeOutsideClick = () => {
            if (outsideClickHandler) {
                document.removeEventListener('pointerdown', outsideClickHandler);
                outsideClickHandler = null;
            }
        };

        const addOutsideClick = (fw: Element) => {
            if (outsideClickHandler) return;
            outsideClickHandler = (e: PointerEvent) => {
                if (fw.contains(e.target as Node)) return;
                const textarea = fw.querySelector('textarea') as HTMLTextAreaElement | null;
                if (textarea) {
                    textarea.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', keyCode: 27, bubbles: true, cancelable: true }));
                } else {
                    (this.editor as monaco.editor.IStandaloneCodeEditor | null)?.trigger('', 'closeFindWidget', {});
                }
            };
            document.addEventListener('pointerdown', outsideClickHandler);
        };

        const tryWatch = () => {
            if (findWidgetObserver) return;
            const fw = dom.querySelector('.find-widget');
            if (!fw) return;
            if (fw.classList.contains('visible')) addOutsideClick(fw);
            findWidgetObserver = new MutationObserver(() => {
                if (fw.classList.contains('visible')) {
                    addOutsideClick(fw);
                } else {
                    removeOutsideClick();
                    if (this.vkbVisible) this.closeVkb();
                }
            });
            findWidgetObserver.observe(fw, { attributes: true, attributeFilter: ['class'] });
        };

        // Find widget is added lazily on first use — watch for it to appear.
        const containerObserver = new MutationObserver(tryWatch);
        containerObserver.observe(dom, { childList: true, subtree: true });
        tryWatch();

        this._findWidgetCleanup = () => {
            containerObserver.disconnect();
            findWidgetObserver?.disconnect();
            removeOutsideClick();
        };
    }

    // ── Prop-sync setters ────────────────────────────────────────────────────
    // Called by the container on every render for changed props.
    // Guard against undefined so omitting a prop never resets internal state.

    setDiff(value: boolean | undefined) {
        if (value !== undefined) this.diff = value;
    }

    setOriginal(value: string | undefined) {
        if (value !== undefined) this.original = value;
    }

    setModified(value: string | undefined) {
        if (value !== undefined) this.modified = value;
    }

    setValue(value: string | undefined) {
        if (value !== undefined) this.value = value;
    }

    setLanguage(value: string | undefined) {
        if (value !== undefined) this.language = value;
    }

    setReadOnly(value: boolean | undefined) {
        if (value !== undefined) this.readOnly = value;
    }

    setOnChange(value: ((v: string) => void) | undefined) {
        if (value !== undefined) this.onChange = value;
    }

    setEnableConflictLens(value: boolean | undefined) {
        if (value !== undefined) this.enableConflictLens = value;
    }

    setDiffModifiedEditable(value: boolean | undefined) {
        if (value !== undefined) this.diffModifiedEditable = value;
    }

    setGetConflicts(value: (() => any[]) | null | undefined) {
        if (value !== undefined) this.getConflicts = value;
    }

    setStashConflict(value: boolean | undefined) {
        if (value !== undefined) this.stashConflict = value;
    }

    setSaveNow(value: (() => void) | undefined) {
        if (value !== undefined) this.saveNow = value;
    }

    setDecorationRanges(value: LineDecoration[] | undefined) {
        if (value !== undefined) this.decorationRanges = value;
    }

    setScrollToLine(value: number | null | undefined) {
        if (value !== undefined) this.scrollToLine = value;
    }

    setOnScrollToLineDone(value: (() => void) | undefined) {
        if (value !== undefined) this.onScrollToLineDone = value;
    }

    setOnCtrlClickSymbol(value: ((symbol: string, line: number, col: number) => void) | undefined) {
        if (value !== undefined) this.onCtrlClickSymbol = value;
    }

    setViewStateKey(value: string | undefined) {
        if (value !== undefined) this.viewStateKey = value;
    }

    setDisableContextMenu(value: boolean | undefined) {
        if (value !== undefined) this.disableContextMenu = value;
    }

    setDisableSelection(value: boolean | undefined) {
        if (value !== undefined) this.disableSelection = value;
    }

    // Special case: guard against undefined so an omitted fontSize prop doesn't
    // overwrite the in-progress pinch-zoom value.
    setFontSize(value: number | undefined) {
        if (value !== undefined) this.fontSize = value;
    }

    setOnFontSizeChange(value: ((size: number) => void) | undefined) {
        if (value !== undefined) this.onFontSizeChange = value;
    }

    setHidden(value: boolean | undefined) {
        if (value !== undefined) this.hidden = value;
    }

    setWordWrap(value: boolean | undefined) {
        if (value !== undefined) this.wordWrap = value;
    }

    setScrollSyncKey(value: string | undefined) {
        if (value !== undefined) this.scrollSyncKey = value;
    }

    setScrollSyncSection(value: ("code" | "review") | undefined) {
        if (value !== undefined) this.scrollSyncSection = value;
    }

    // Returns the find widget's textarea if the find widget is currently open.
    // Monaco uses flexibleHeight:true on the find input, so it renders a <textarea>.
    getFindInput(): HTMLTextAreaElement | null {
        const ed = this.diff
            ? (this.editor as monaco.editor.IStandaloneDiffEditor | null)?.getModifiedEditor() ?? null
            : this.editor as monaco.editor.IStandaloneCodeEditor | null;
        if (!ed) return null;
        const dom = ed.getDomNode();
        if (!dom) return null;
        const fw = dom.querySelector('.find-widget');
        if (!fw?.classList.contains('visible')) return null;
        return fw.querySelector('.monaco-findInput textarea') as HTMLTextAreaElement | null;
    }

    handleVkbKey(key: string) {
        const findInput = this.getFindInput();
        if (findInput) {
            this.handleVkbKeyInFindInput(key, findInput);
            return;
        }
        if (this.diff) {
            if (!this.diffModifiedEditable) return;
            const diffEd = this.editor as monaco.editor.IStandaloneDiffEditor | null;
            if (!diffEd) return;
            const ed = diffEd.getModifiedEditor();
            const selection = ed.getSelection() ?? new monaco.Range(1, 1, 1, 1);
            if (key === 'Backspace') {
                if (!selection.isEmpty()) {
                    ed.executeEdits('vkb', [{ range: selection, text: '', forceMoveMarkers: true }]);
                } else {
                    const model = ed.getModel();
                    if (!model) return;
                    const pos = selection.getStartPosition();
                    const offset = model.getOffsetAt(pos);
                    if (offset === 0) return;
                    const prevPos = model.getPositionAt(offset - 1);
                    const deleteRange = new monaco.Range(prevPos.lineNumber, prevPos.column, pos.lineNumber, pos.column);
                    ed.executeEdits('vkb', [{ range: deleteRange, text: '', forceMoveMarkers: true }]);
                }
            } else {
                ed.executeEdits('vkb', [{ range: selection, text: key === 'Enter' ? '\n' : key, forceMoveMarkers: true }]);
            }
            return;
        }
        if (this.readOnly) return;
        const ed = this.editor as monaco.editor.IStandaloneCodeEditor | null;
        if (!ed) return;
        const selection = ed.getSelection() ?? new monaco.Range(1, 1, 1, 1);
        if (key === 'Backspace') {
            if (!selection.isEmpty()) {
                ed.executeEdits('vkb', [{ range: selection, text: '', forceMoveMarkers: true }]);
            } else {
                const model = ed.getModel();
                if (!model) return;
                const pos = selection.getStartPosition();
                const offset = model.getOffsetAt(pos);
                if (offset === 0) return;
                const prevPos = model.getPositionAt(offset - 1);
                const deleteRange = new monaco.Range(prevPos.lineNumber, prevPos.column, pos.lineNumber, pos.column);
                ed.executeEdits('vkb', [{ range: deleteRange, text: '', forceMoveMarkers: true }]);
            }
        } else {
            const text = key === 'Enter' ? '\n' : key;
            ed.executeEdits('vkb', [{ range: selection, text, forceMoveMarkers: true }]);
        }
    }

    handleVkbKeyInFindInput(key: string, input: HTMLTextAreaElement) {
        if (key === 'Enter') {
            // "Find next" — Monaco handles Enter on the find input
            input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }));
            return;
        }
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? input.value.length;
        let newValue: string;
        let newCursor: number;
        if (key === 'Backspace') {
            if (start !== end) {
                newValue = input.value.slice(0, start) + input.value.slice(end);
                newCursor = start;
            } else if (start > 0) {
                newValue = input.value.slice(0, start - 1) + input.value.slice(start);
                newCursor = start - 1;
            } else {
                return;
            }
        } else {
            newValue = input.value.slice(0, start) + key + input.value.slice(end);
            newCursor = start + key.length;
        }
        input.value = newValue;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.setSelectionRange(newCursor, newCursor);
    }

    triggerFind() {
        const ed = this.editor as monaco.editor.IStandaloneCodeEditor | null;
        if (!ed) return;
        ed.getAction('actions.find')?.run();
        this.openVkb();
    }

    applyFontSize(size: number) {
        this.fontSize = size;
        this.onFontSizeChange?.(size);
    }

    applyMagnifierState(state: MagnifierState | null) {
        this.magnifierState = state;
    }

    applyVkbVisible(visible: boolean) {
        this.vkbVisible = visible;
    }

    applyVkbCollapsed(collapsed: boolean) {
        this.vkbCollapsed = collapsed;
    }

    openVkb() {
        if (!this.vkbVisible) {
            history.pushState({ vkb: true }, '');
            this._vkbHistoryPushed = true;
        }
        this.vkbVisible = true;
    }

    closeVkb() {
        if (this._vkbHistoryPushed) {
            this._vkbHistoryPushed = false;
            history.replaceState({ pwa_guard: true }, '');
        }
        this.vkbVisible = false;
        this.vkbCollapsed = true;
    }

    handlePopState() {
        if (!this.vkbVisible) return;
        // Back button pressed — collapse first (splitter is always enabled in MonacoEditor).
        if (!this.vkbCollapsed) {
            this.vkbCollapsed = true;
            history.pushState({ vkb: true }, '');
            this._vkbHistoryPushed = true;
            return;
        }
        this._vkbHistoryPushed = false;
        this.vkbVisible = false;
        this.vkbCollapsed = true;
    }
}
